// CMS registry — the lookup that powers automatic connectedness.
//
// Every published article (by title + author-defined aliases) and a curated set
// of app sections become "link targets". The renderer scans article text against
// this registry and auto-links the first mention of each target — so a new
// article wires itself into the existing ones with no back-editing.
//
// Built entirely in the browser from the already-fetched article set: no hook,
// no sync, and new content is reflected the instant it's published.

// Curated app destinations (non-article surfaces) worth linking to. Aliases are
// kept distinctive on purpose — generic words like "article" or "word" would
// over-link and create noise.
const APP_SECTIONS = [
  { route: "search", title: "Verbs", aliases: ["verbs", "verb", "conjugation", "conjugate", "subjunctive", "imperative"] },
  { route: "structure", title: "Sentence Structure", aliases: ["sentence structure", "word order"] },
  { route: "nouns", title: "Nouns", aliases: ["nouns"] },
  { route: "adjectives", title: "Adjectives", aliases: ["adjectives", "adjective agreement"] },
  { route: "pronouns", title: "Pronouns", aliases: ["pronouns", "object pronoun", "subject pronoun"] },
  { route: "adverbs", title: "Adverbs", aliases: ["adverbs"] },
  { route: "prepositions", title: "Prepositions", aliases: ["prepositions", "por vs para", "personal a"] },
  { route: "conjunctions", title: "Conjunctions", aliases: ["conjunctions"] },
  { route: "interjections", title: "Interjections", aliases: ["interjections"] },
  { route: "objects", title: "Direct & Indirect Objects", aliases: ["direct object", "indirect object"] },
  { route: "locutions", title: "Locutions", aliases: ["locutions", "locución"] },
  { route: "vocabulary", title: "Vocabulary", aliases: ["vocabulary"] },
];

// Terms too generic to ever auto-link, even if an author lists them.
const STOPWORDS = new Set(["a", "el", "la", "los", "las", "article", "articles", "word", "words", "the", "spanish"]);

function cleanAliases(list) {
  const out = [];
  const seen = new Set();
  for (const raw of list) {
    const a = String(raw || "").trim().toLowerCase();
    if (a.length < 3) continue; // too short to match safely
    if (STOPWORDS.has(a)) continue;
    if (seen.has(a)) continue;
    seen.add(a);
    out.push(a);
  }
  return out;
}

// Parse an article's `aliases` CSV plus its title into a clean alias list.
function articleAliases(article) {
  const fromCsv = String(article.aliases || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return cleanAliases([article.title, ...fromCsv]);
}

// Build the registry. `currentSlug` is excluded so an article never links to
// itself. Drafts are never link targets. Entries are sorted so the longest
// alias wins first (greedy, avoids "verb" stealing a match from "verb tense").
export function buildRegistry(articles, currentSlug) {
  const entries = [];

  for (const a of articles || []) {
    if (!a || a.slug === currentSlug) continue;
    if (a.status && a.status !== "published") continue;
    const aliases = articleAliases(a);
    if (!aliases.length) continue;
    entries.push({ kind: "article", target: a.slug, title: a.title || a.slug, aliases });
  }

  for (const s of APP_SECTIONS) {
    if (s.route === currentSlug) continue;
    entries.push({ kind: "view", target: s.route, title: s.title, aliases: cleanAliases(s.aliases) });
  }

  // Flatten to {alias, entry} pairs, longest alias first.
  const pairs = [];
  for (const e of entries) {
    for (const alias of e.aliases) pairs.push({ alias, entry: e });
  }
  pairs.sort((a, b) => b.alias.length - a.alias.length);
  return pairs;
}
